'use client';

import {
  type PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import type {
  UploadJobItemProgressPayload,
  UxpTransferState,
} from '@frameio/graphql-client/generated';
import { useTrack, useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { formatFileSizeInMB } from '@frameio/utils/formatters';
import { ToastAlert, useToast } from '@frameio/vapor';
import { DocumentProgressStatus } from '../../shared/components/DocumentExports/types';
import { isFailedDocumentProgressStatus } from '../../shared/components/DocumentExports/utils';
import { useCancelUploadTransferJobMutation } from '../../shared/gql';
import { useDatadogLogger } from '../../shared/hooks/useDatadogLogger';
import { useCreateCommentsFromMarkers } from '../hooks/useCreateCommentsFromMarkers';
import { useRenderJobEvents } from '../hooks/useRenderJobEvents';
import { useSaveExportMetadata } from '../hooks/useSaveExportMetadata';
import { useUploadJobItemProgressEvent } from '../hooks/useUploadJobItemProgressEvent';
import type {
  CompositionExportProgressItem,
  CompositionExportProgressItems,
} from '../types/exportProgressTypes';
import {
  useGetCompositionExportJobSettings,
  useSetRenderingCount,
} from './CompositionExportContext';

type RegisterRenderedAssetParams = {
  assetId: string;
  jobId: string;
  fileName: string;
  folderId: string;
  compositionId: string;
  projectId?: string;
  workAreaDuration: number | null;
  workAreaStart: number | null;
  framesPerSecond?: number | null;
};

type PendingAssetExport = {
  compositionId: string;
  projectId?: string;
  workAreaDuration: number | null;
  workAreaStart: number | null;
  framesPerSecond?: number | null;
};

type PendingAssetExportMap = {
  [assetId: string]: PendingAssetExport;
};

type CompositionExportProgressContextType = {
  cancelInProgressExports: () => Promise<void>;
  clearFailedExports: () => void;
  isCancelingAllUploads: boolean;
  items: CompositionExportProgressItems;
  registerRenderedAsset: (params: RegisterRenderedAssetParams) => void;
};

const DEFAULT_VALUE: CompositionExportProgressContextType = {
  cancelInProgressExports: async () => undefined,
  clearFailedExports: () => undefined,
  isCancelingAllUploads: false,
  items: {},
  registerRenderedAsset: () => undefined,
};

export const CompositionExportProgressContext =
  createContext<CompositionExportProgressContextType>(DEFAULT_VALUE);

const getDocumentProgressStatus = (
  state: UxpTransferState | null | undefined,
  percentage: number | null | undefined
): DocumentProgressStatus => {
  if (state === 'canceled') return DocumentProgressStatus.Canceled;
  if (state === 'failed') return DocumentProgressStatus.Failed;
  if (state === 'completed' || percentage === 1)
    return DocumentProgressStatus.Completed;
  return DocumentProgressStatus.Uploading;
};

const buildUploadTrackData = (params: {
  jobId?: string | null;
  assetId?: string | null;
  bytesTotal?: number | null;
}) => ({
  source: 'AeUXP',
  transfer_job_id: params.jobId ?? undefined,
  asset_id: params.assetId ?? undefined,
  total_file_size: params.bytesTotal
    ? formatFileSizeInMB(params.bytesTotal)
    : undefined,
});

const isUploadJobComplete = (event: UploadJobItemProgressPayload) =>
  event.state === 'completed' || event.percentage === 1;

export const CompositionExportProgressProvider = ({
  children,
}: NonNullable<PropsWithChildren>) => {
  const [allItems, setItems] = useState<CompositionExportProgressItems>({});
  const [isCancelingAllUploads, setIsCancelingAllUploads] = useState(false);
  const pendingAssetExportRef = useRef<PendingAssetExportMap>({});
  const saveExportMetadata = useSaveExportMetadata();
  const datadogLogger = useDatadogLogger();
  const track = useTrack();
  const trackUnsafe = useTrackUnsafe();
  const { createCommentsFromMarkers } = useCreateCommentsFromMarkers();
  const getJobSettings = useGetCompositionExportJobSettings();
  const { addToast } = useToast();
  const setRenderingCount = useSetRenderingCount();
  const [cancelUploadTransferJob] = useCancelUploadTransferJobMutation({
    context: { resolveWithUXP: true },
  });

  const items = useMemo(() => {
    return Object.entries(allItems).reduce<CompositionExportProgressItems>(
      (acc, [assetId, item]) => {
        if (
          item.progress === 1 ||
          item.status === DocumentProgressStatus.Completed
        )
          return acc;
        return { ...acc, [assetId]: item };
      },
      {}
    );
  }, [allItems]);

  const updateItem = useCallback(
    (assetId: string, updates: Partial<CompositionExportProgressItem>) => {
      setItems((prev) => {
        if (!prev[assetId]) return prev;
        return { ...prev, [assetId]: { ...prev[assetId], ...updates } };
      });
    },
    []
  );

  const registerRenderedAsset = useCallback(
    ({
      assetId,
      jobId,
      fileName,
      folderId,
      compositionId,
      projectId,
      workAreaDuration,
      workAreaStart,
      framesPerSecond,
    }: RegisterRenderedAssetParams) => {
      setItems((prev) => ({
        ...prev,
        [assetId]: {
          id: assetId,
          assetId,
          compositionId,
          jobId,
          fileName,
          folderId,
          projectId,
          status: DocumentProgressStatus.Queued,
          createdAt: Date.now(),
        },
      }));
      pendingAssetExportRef.current[assetId] = {
        compositionId,
        projectId,
        workAreaDuration,
        workAreaStart,
        framesPerSecond: framesPerSecond ?? null,
      };

      const trackData = buildUploadTrackData({ jobId, assetId });
      trackUnsafe('upload-started', trackData);
      datadogLogger.info('integrations-upload-started', {
        uxpUploads: trackData,
      });
    },
    [datadogLogger, trackUnsafe]
  );

  const maybeCreateCommentsFromMarkers = useCallback(
    (
      event: UploadJobItemProgressPayload,
      pending: PendingAssetExport | undefined
    ) => {
      if (event.state !== 'completed' || !event.assetId) return;

      const settings = getJobSettings(event.jobId);
      if (pending?.compositionId && settings?.exportMarkersAsCommentsEnabled) {
        createCommentsFromMarkers({
          assetId: event.assetId,
          documentId: pending.compositionId,
          workAreaDuration: pending.workAreaDuration,
          workAreaStart: pending.workAreaStart,
          framesPerSecond: pending.framesPerSecond,
        }).catch((error) => {
          datadogLogger.error(
            'integrations-ae-marker-comments-after-upload-failed',
            {
              assetId: event.assetId,
              compositionId: pending.compositionId,
              error: error instanceof Error ? error.message : String(error),
            }
          );
        });
      }
    },
    [createCommentsFromMarkers, datadogLogger, getJobSettings]
  );

  const onUploadProgress = useCallback(
    (event: UploadJobItemProgressPayload) => {
      const item = items[event.assetId];
      if (!item) return;

      if (
        item.status === DocumentProgressStatus.Canceled &&
        event.state !== 'canceled'
      ) {
        return;
      }

      const previousStatus = item.status;

      updateItem(event.assetId, {
        assetId: event.assetId,
        bytesSent: event.bytesSent,
        bytesTotal: event.bytesTotal,
        progress: event.percentage,
        status: getDocumentProgressStatus(event.state, event.percentage),
      });

      if (
        event.state === 'failed' &&
        previousStatus !== DocumentProgressStatus.Failed
      ) {
        const trackData = buildUploadTrackData({
          jobId: event.jobId,
          assetId: event.assetId,
          bytesTotal: event.bytesTotal,
        });
        trackUnsafe('upload-failed', trackData);
        datadogLogger.info('integrations-upload-failed', {
          uxpUploads: trackData,
        });
      }

      if (
        isUploadJobComplete(event) &&
        previousStatus !== DocumentProgressStatus.Completed
      ) {
        const trackData = buildUploadTrackData({
          jobId: event.jobId,
          assetId: event.assetId,
          bytesTotal: event.bytesTotal,
        });
        trackUnsafe('upload-completed', trackData);
        datadogLogger.info('integrations-upload-completed', {
          uxpUploads: trackData,
        });
      }

      if (
        event.state === 'canceled' &&
        previousStatus !== DocumentProgressStatus.Canceled
      ) {
        const trackData = buildUploadTrackData({
          jobId: event.jobId,
          assetId: event.assetId,
          bytesTotal: event.bytesTotal,
        });
        track('upload-canceled', trackData);
        datadogLogger.info('integrations-upload-canceled', {
          uxpUploads: trackData,
        });
      }

      if (event.state === 'canceled' || event.state === 'failed') {
        delete pendingAssetExportRef.current[event.assetId];
        return;
      }

      if (!isUploadJobComplete(event)) return;

      const pending = pendingAssetExportRef.current[event.assetId];
      delete pendingAssetExportRef.current[event.assetId];

      maybeCreateCommentsFromMarkers(event, pending);

      if (!pending?.projectId) return;

      saveExportMetadata({
        compositionId: pending.compositionId,
        assetId: event.assetId,
        projectId: pending.projectId,
        workAreaStart: pending.workAreaStart,
        workAreaDuration: pending.workAreaDuration,
      }).catch((error: unknown) => {
        datadogLogger.error('integrations-ae-save-export-metadata-failed', {
          assetId: event.assetId,
          compositionId: pending.compositionId,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    },
    [
      items,
      updateItem,
      saveExportMetadata,
      datadogLogger,
      maybeCreateCommentsFromMarkers,
      track,
      trackUnsafe,
    ]
  );

  useUploadJobItemProgressEvent(onUploadProgress);

  useRenderJobEvents({
    getJobSettings,
    registerRenderedAsset,
    setRenderingCount,
    onRenderError: (message) => {
      addToast(<ToastAlert title={message} variant="error" />, {
        duration: 5000,
      });
    },
  });

  const clearFailedExports = useCallback(() => {
    setItems((prev) => {
      const next: CompositionExportProgressItems = {};
      Object.entries(prev).forEach(([assetId, item]) => {
        if (!isFailedDocumentProgressStatus(item.status)) {
          next[assetId] = item;
        }
      });
      return next;
    });
  }, []);

  const cancelInProgressExports = useCallback(async () => {
    const activeJobIds = new Set<string>();
    Object.values(items).forEach((item) => {
      if (item.jobId && !isFailedDocumentProgressStatus(item.status)) {
        activeJobIds.add(item.jobId);
      }
    });
    if (activeJobIds.size === 0) return;

    setIsCancelingAllUploads(true);
    setItems((prev) => {
      const next: CompositionExportProgressItems = { ...prev };
      Object.entries(prev).forEach(([assetId, item]) => {
        if (item.jobId && activeJobIds.has(item.jobId)) {
          next[assetId] = {
            ...item,
            status: DocumentProgressStatus.Canceled,
          };
        }
      });
      return next;
    });

    try {
      await Promise.allSettled(
        Array.from(activeJobIds).map((jobId) =>
          cancelUploadTransferJob({ variables: { jobId } }).catch((error) => {
            datadogLogger.error('integrations-ae-cancel-upload-failed', {
              jobId,
              error: error instanceof Error ? error.message : String(error),
            });
          })
        )
      );
    } finally {
      setIsCancelingAllUploads(false);
    }
  }, [cancelUploadTransferJob, datadogLogger, items]);

  const value = useMemo(
    () => ({
      cancelInProgressExports,
      clearFailedExports,
      isCancelingAllUploads,
      items,
      registerRenderedAsset,
    }),
    [
      cancelInProgressExports,
      clearFailedExports,
      isCancelingAllUploads,
      items,
      registerRenderedAsset,
    ]
  );

  return (
    <CompositionExportProgressContext.Provider value={value}>
      {children}
    </CompositionExportProgressContext.Provider>
  );
};

export const useCompositionExportProgressItems = () =>
  useContextSelector(CompositionExportProgressContext, (ctx) => ctx.items);

export const useRegisterRenderedAsset = () =>
  useContextSelector(
    CompositionExportProgressContext,
    (ctx) => ctx.registerRenderedAsset
  );

export const useCancelInProgressCompositionExports = () =>
  useContextSelector(
    CompositionExportProgressContext,
    (ctx) => ctx.cancelInProgressExports
  );

export const useClearFailedCompositionExports = () =>
  useContextSelector(
    CompositionExportProgressContext,
    (ctx) => ctx.clearFailedExports
  );

export const useIsCancelingAllCompositionExportUploads = () =>
  useContextSelector(
    CompositionExportProgressContext,
    (ctx) => ctx.isCancelingAllUploads
  );

/**
 * True when there are in-progress items and every one is in a terminal
 * (canceled / failed) status — i.e. nothing left to cancel, only to clear.
 */
export const useAreAllCompositionExportsSettled = () =>
  useContextSelector(CompositionExportProgressContext, (ctx) => {
    const values = Object.values(ctx.items);
    return (
      values.length > 0 &&
      values.every((item) => isFailedDocumentProgressStatus(item.status))
    );
  });
